import type { SnippetNode } from './types';

/**
 * Aus den Bausteinzeilen des Servers den Baum machen, den das Kommentarblatt anzeigt.
 *
 * **Bausteine sind vorgegeben** (Klarstellung des Nutzers, 2026-09-16): Der Server liefert sie
 * mit der Prüfung, das Gerät kann sie **übernehmen** — nicht anlegen, nicht ändern. Deshalb
 * gibt es hier nur den Weg herein.
 *
 * ```
 * PredefinedCommentPhraseList        die Liste
 *   └ PredefinedCommentPhrase        ihre Einträge, über parentPredefinedCommentPhraseID
 *       └ PredefinedCommentPhrase    beliebig tief verschachtelt
 * ExamItemPredefinedCommentPhraseList   welche Liste an welcher Aufgabe hängt
 * ```
 *
 * ⚠️ **`name` ist die Beschriftung, `fullText` der Text, der eingefügt wird.** Im Classic-Bestand
 * sind sie oft verschieden: der Baum zeigt „Roter Faden", eingefügt wird ein ganzer Satz. Fehlt
 * `fullText`, ist die Beschriftung selbst der Baustein — ein Eintrag ohne beides wäre eine
 * Zeile, die nichts tut.
 */

/** Eine Bausteinzeile, wie sie in der Ablage steht. */
export interface Bausteinzeile {
  id: string;
  parentPredefinedCommentPhraseID: string | null;
  name: string;
  fullText: string;
  orderNo: number;
}

export function baueBausteinbaum(zeilen: Bausteinzeile[]): SnippetNode[] {
  const vorhanden = new Set(zeilen.map((z) => z.id));
  const kinder = new Map<string, Bausteinzeile[]>();

  for (const zeile of zeilen) {
    // ⚠️ Ein Elternteil, das nicht mitgeliefert wurde, zählt als Wurzel. Sonst verschwände der
    // Eintrag lautlos — und niemand sucht einen Baustein, von dem er nicht weiss, dass es ihn
    // gibt.
    const eltern =
      zeile.parentPredefinedCommentPhraseID && vorhanden.has(zeile.parentPredefinedCommentPhraseID)
        ? zeile.parentPredefinedCommentPhraseID
        : '';
    const liste = kinder.get(eltern);
    if (liste) liste.push(zeile);
    else kinder.set(eltern, [zeile]);
  }

  for (const liste of kinder.values()) {
    liste.sort((a, b) => a.orderNo - b.orderNo || a.name.localeCompare(b.name));
  }

  /**
   * ⚠️ **Gegen Kreise gesichert.** `parentPredefinedCommentPhraseID` zeigt auf dieselbe
   * Tabelle; zwei Einträge, die sich gegenseitig als Elternteil führen, drehten sich hier
   * ewig. Am Prüfungstag wäre das ein Blatt, das sich nicht mehr öffnen lässt.
   */
  const gesehen = new Set<string>();

  const baue = (elternId: string): SnippetNode[] =>
    (kinder.get(elternId) ?? []).flatMap<SnippetNode>((zeile) => {
      if (gesehen.has(zeile.id)) return [];
      gesehen.add(zeile.id);

      const unter = baue(zeile.id);
      if (unter.length > 0) {
        return [{ label: zeile.name || zeile.fullText, children: unter } satisfies SnippetNode];
      }
      const text = zeile.fullText || zeile.name;
      if (text === '') return [];
      // Ein Blatt trägt beides: die kurze Beschriftung für den Baum, den vollen Text zum
      // Einfügen. Sind sie gleich, fällt das in der Anzeige nicht auf.
      return [{ label: zeile.name || text, text } satisfies SnippetNode];
    });

  return baue('');
}

/** Wie viele Bausteine wirklich einfügbar sind — die Zahl in der Kopfzeile der Leiste. */
export function zaehleBausteine(knoten: SnippetNode[]): number {
  return knoten.reduce(
    (summe, k) => summe + (k.children ? zaehleBausteine(k.children) : 1),
    0,
  );
}
